import { Injectable } from '@angular/core';
import * as MarkReadStatusActions from '@app/pcs/actions/mark-read-status';
import { MarkReadStatusActionTypes } from '@app/pcs/actions/mark-read-status';
import { DossierSearchItem, GridType } from '@app/pcs/models';
import { MarkReadStatusDataService } from '@app/pcs/services/mark-read-status-data.service';
import { updateGridDataForSelection } from '@app/pcs/util';
import * as fromRoot from '@app/reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { getSelectedData } from 'ngrx-data-grid';
import * as R from 'ramda';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, withLatestFrom } from 'rxjs/operators';

interface DossierVersion {
  dossierId: number;
  version: number;
}

type GetDossierVersion = (dossierSearchItem: DossierSearchItem[]) => DossierVersion[];
const getDossierVersion: GetDossierVersion = R.map(R.pick(['dossierId', 'version']));

type GetDossierIds = (dossierVersion: DossierVersion[]) => number[];
const getDossierIds: GetDossierIds = R.map(R.prop('dossierId'));

const updateMarkAsRead = dossierSearchItem =>
  R.mergeRight(dossierSearchItem, {lastReadVersion: dossierSearchItem.version});
const updateMarkAsUnread = dossierSearchItem =>
  R.mergeRight(dossierSearchItem, {lastReadVersion: R.max(1, dossierSearchItem.version - 1)});

@Injectable()
export class MarkReadStatusEffects {
  gridState$ = this.store.pipe(select(fromRoot.getGridState));

  selectedDossierVersions$ = this.gridState$.pipe(
    select(getSelectedData, {gridName: GridType.Search}),
    map(getDossierVersion)
  );

  markAsRead$ = createEffect(() => this.actions$.pipe(
    ofType<MarkReadStatusActions.MarkAsRead>(MarkReadStatusActionTypes.MarkAsRead),
    withLatestFrom(this.selectedDossierVersions$),
    exhaustMap(([, dossierVersions]) => this.markReadStatusDataService.markAsRead(dossierVersions)
      .pipe(
        switchMap(() => [updateGridDataForSelection(getDossierIds(dossierVersions), updateMarkAsRead)]),
        catchError((error: Error) => of(new MarkReadStatusActions.MarkAsReadFail(error)))
      )
    )
  ));

  markAsUnread$ = createEffect(() => this.actions$.pipe(
    ofType<MarkReadStatusActions.MarkAsUnread>(MarkReadStatusActionTypes.MarkAsUnread),
    withLatestFrom(this.selectedDossierVersions$),
    exhaustMap(([, dossierVersions]) => this.markReadStatusDataService.markAsUnread(dossierVersions)
      .pipe(
        switchMap(() => [updateGridDataForSelection(getDossierIds(dossierVersions), updateMarkAsUnread)]),
        catchError((error: Error) => of(new MarkReadStatusActions.MarkAsReadFail(error)))
      )
    )
  ));

  constructor(private actions$: Actions,
    private store: Store<any>,
    private markReadStatusDataService: MarkReadStatusDataService) {
  }
}
